import React, { useEffect, useState } from "react";
import { Store, Plus, Pencil, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Spinner, PageHeader, StatusPill, Empty, BRAND_DOT } from "@/components/shared";
import { FieldW, SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const COLORS = ["amber", "emerald", "rose", "sky", "violet"];

export default function Brands() {
  const [list, setList] = useState(null);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [delItem, setDelItem] = useState(null);

  const del = async () => {
    try {
      await api.delete(`/brands/${delItem.id}`);
      toast.success("Brand deleted");
      setDelItem(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setDelItem(null); }
  };

  const load = () => Promise.all([api.get("/brands"), api.get("/clients")]).then(([b, c]) => { setList(b.data); setClients(c.data); });
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const empty = () => ({ client_id: clients[0]?.id || "", name: "", cuisine: "", status: "Active", op_start: "10:00", op_end: "23:00", daily_capacity: 100, color: "amber", notes: "", logo: "" });
  const openNew = () => { setForm(empty()); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...b }); setEditId(b.id); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, daily_capacity: Number(form.daily_capacity) };
      if (editId) await api.put(`/brands/${editId}`, payload);
      else await api.post("/brands", payload);
      toast.success(editId ? "Brand updated" : "Brand created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  if (!list) return <Spinner />;

  return (
    <div>
      <PageHeader title="Brands" subtitle="Virtual brands operating from the Dish Factory kitchen">
        <Button onClick={openNew} data-testid="add-brand-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
          <Plus className="h-4 w-4" /> New Brand
        </Button>
      </PageHeader>

      {list.length === 0 ? (
        <Empty icon={Store} title="No brands yet" />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((b) => (
            <div key={b.id} className="card-surface p-5" data-testid={`brand-card-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`h-3 w-3 rounded-full ${BRAND_DOT[b.color] || "bg-slate-400"}`} />
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-white truncate">{b.name}</h3>
                    <p className="text-xs text-muted-foreground truncate">{clientName(b.client_id)}</p>
                  </div>
                </div>
                <StatusPill status={b.status} />
              </div>
              <p className="text-sm text-slate-300 mt-3">{b.cuisine}</p>
              <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <div><p className="text-[10px] uppercase text-muted-foreground font-mono">Hours</p><p className="text-slate-200 font-mono">{b.op_start}–{b.op_end}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground font-mono">Daily capacity</p><p className="text-slate-200 font-mono">{b.daily_capacity} orders</p></div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => openEdit(b)} data-testid={`edit-brand-${b.id}`} className="gap-1.5 flex-1">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button variant="outline" size="icon" onClick={() => setDelItem(b)} data-testid={`delete-brand-${b.id}`} className="h-9 w-9">
                  <Trash2 className="h-4 w-4 text-rose-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-card border-border">
          <DialogHeader><DialogTitle>{editId ? "Edit Brand" : "New Brand"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldW label="Client">
              <SelectBox value={form.client_id} onChange={(v) => set("client_id", v)} options={clients.map((c) => ({ value: c.id, label: c.name }))} testid="brand-client" />
            </FieldW>
            <FieldW label="Brand name"><Input data-testid="brand-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></FieldW>
            <FieldW label="Cuisine / category"><Input value={form.cuisine || ""} onChange={(e) => set("cuisine", e.target.value)} /></FieldW>
            <FieldW label="Colour">
              <SelectBox value={form.color} onChange={(v) => set("color", v)} options={COLORS} />
            </FieldW>
            <FieldW label="Operating start"><Input type="time" value={form.op_start || ""} onChange={(e) => set("op_start", e.target.value)} /></FieldW>
            <FieldW label="Operating end"><Input type="time" value={form.op_end || ""} onChange={(e) => set("op_end", e.target.value)} /></FieldW>
            <FieldW label="Daily max capacity"><Input type="number" data-testid="brand-capacity" value={form.daily_capacity} onChange={(e) => set("daily_capacity", e.target.value)} /></FieldW>
            <FieldW label="Status">
              <SelectBox value={form.status} onChange={(v) => set("status", v)} options={["Active", "Inactive"]} />
            </FieldW>
            <FieldW label="Notes" full><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} /></FieldW>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.name || !form.client_id} data-testid="save-brand-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delItem?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the brand and is only allowed when no menu items or orders are linked.
              If any exist, set the brand to Inactive instead to preserve history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="confirm-delete-brand" className="bg-rose-600 hover:bg-rose-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
